/**
 * Formatação compartilhada das telas de Oficina Externa (at-114, ADR 0059).
 *
 * Vive num arquivo só porque as três telas (leitura, inspetor e o bloco dentro dos modais da
 * oficina interna) precisam dizer a MESMA coisa sobre "dias sem visita" — e a frase errada aqui
 * é a diferença entre "ninguém foi lá" e "foram hoje".
 */

/**
 * 🔴 `null` NUNCA vira "0 dias".
 *
 * `null` significa que o veículo está cadastrado na oficina e NINGUÉM registrou visita nele
 * ainda; `0` significa que foi visitado hoje. São os dois extremos opostos, e confundi-los faz o
 * caso mais grave da cobertura passar por resolvido.
 */
export const diasSemVisitaTexto = (dias: number | null | undefined): string => {
  if (dias === null || dias === undefined) return 'Nunca visitado';
  if (dias === 0) return 'Hoje';
  return dias === 1 ? 'há 1 dia' : `há ${dias} dias`;
};

export interface SeloDeAtraso {
  classe: string;
  titulo: string;
}

/**
 * A cor do selo. Usa a paleta compartilhada (`shared/ui/badge-status.css`) — nunca classe de
 * status própria por tela.
 *
 * ⚠️ Vermelho é para quem NUNCA foi visitado, não para o mais atrasado: o atraso longo é um
 * problema de agenda; a ausência total de visita é um veículo sobre o qual o sistema não sabe
 * absolutamente nada.
 */
export const seloDeAtraso = (
  dias: number | null | undefined,
  diasAlerta: number,
): SeloDeAtraso => {
  if (dias === null || dias === undefined) {
    return {
      classe: 'badge-vermelho',
      titulo: 'Cadastrado na oficina, mas nenhuma visita foi registrada até agora.',
    };
  }
  if (dias >= diasAlerta) {
    return {
      classe: 'badge-laranja',
      titulo: `Sem visita há ${dias} dias (o limite configurado é de ${diasAlerta}).`,
    };
  }
  return { classe: 'badge-verde', titulo: 'Visitado dentro do prazo configurado.' };
};

/** Data curta, ou o traço — nunca uma data inventada a partir de string inválida. */
export const dataCurta = (iso?: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pt-BR');
};

/** Data e hora, para quando a distinção entre visita informada e registro importa. */
export const dataHora = (iso?: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '-'
    : `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      })}`;
};

/** "1 oficina" / "3 oficinas" — nunca "3 oficina(s)", que é texto de sistema, não de tela. */
export const plural = (n: number, singular: string, pluralForm: string): string =>
  `${n} ${n === 1 ? singular : pluralForm}`;

/** Hoje em `YYYY-MM-DD`, no fuso do aparelho — o padrão dos campos de data das telas. */
export const hojeIso = (): string => {
  const agora = new Date();
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');
  return `${agora.getFullYear()}-${mes}-${dia}`;
};

/**
 * Como a resposta de cada tipo de item do checklist vira texto.
 *
 * Vive aqui porque DUAS superfícies a usam — o modal do veículo e a aba "Minhas visitas" —, e a
 * mesma resposta tem de sair igual nas duas: um `true` lido como "true" numa tela e "Sim" na
 * outra é a divergência que faz alguém duvidar do registro.
 */
export const valorLegivel = (resposta: {
  valor: unknown;
  tipo?: string;
}): string => {
  const { valor, tipo } = resposta;
  if (valor === null || valor === undefined) return '-';
  if (tipo === 'SIM_NAO') return valor === true ? 'Sim' : 'Não';
  if (Array.isArray(valor)) return valor.join(', ');
  if (tipo === 'DATA' && typeof valor === 'string') return dataCurta(valor);
  return String(valor);
};
